/**
 * OpenEXR decoder — Phase 3.C / 3.C.1.
 *
 * Wraps the vendored exrs (public/vendor/exrs/, BSD-3-Clause, Rust→
 * WASM build of exrs 1.0.3) in a BLRRD-shaped `decode(blob)` returning
 * the unified Phase 3.A.0 contract.
 *
 * Output is 32-bit linear ACEScg (AP1) Float32 RGBA when the source's
 * primaries can be matched, otherwise linear-Rec709 (the OpenEXR spec
 * default when `chromaticities` is absent). exrs's `decodeRgbaExr`
 * already half→f32 expands and de-interleaves channels, so this
 * decoder mostly does:
 *   1. lazy-load the WASM via the loader-shim pattern (CSP-clean,
 *      Vite-bypassing — same approach as Phase 3.B libraw).
 *   2. parse the EXR header in JS to extract the `chromaticities`
 *      attribute (the exrs WASM API does not surface it).
 *   3. call decodeRgbaExr(buf) (or decodeRgbExr fallback for RGB-only
 *      files; alpha synthesised at 1.0).
 *   4. un-premultiply alpha (Codex 3.C-design: OpenEXR convention is
 *      premultiplied a.k.a. "associated"; BLRRD's kernel + view-
 *      transform expect straight alpha).
 *   5. dispatch on detected colour space:
 *        • linear-Rec709 / sRGB primaries  → return as 'linear-rec709'
 *          (the legacy ingest path runs M_sRGB_to_ACEScg downstream).
 *        • ACES AP0  → apply M_ap0_to_ACEScg in-place, tag 'aces-ap1'.
 *        • ACES AP1  → identity (already ACEScg), tag 'aces-ap1'.
 *        • BT.2020   → apply M_rec2020_to_ACEScg in-place, tag 'aces-ap1'.
 *        • unknown   → derive matrix from chromaticities + Bradford CAT,
 *          apply in-place, tag 'aces-ap1'.
 *      A manual override `decode(blob, { colorSpace: '…' })` short-
 *      circuits detection (used by tests + power-user fallback).
 *
 * Chromaticities access (Phase 3.C.1 research note): the vendored exrs
 * WASM bindings expose only width / height / layerCount / getLayerName
 * / getLayerPixels / getLayerChannelNames — there is no JS-reachable
 * accessor for header attributes such as `chromaticities`. We parse
 * the attribute directly out of the EXR header bytes, which is small
 * (one `chromaticities chromaticities <32-byte-LE-f32×8>` block,
 * typically inside the first ~512 bytes of the file). The format is
 * stable and public-domain spec; no third-party header parser code is
 * consulted.
 *
 * @module src/pipeline/decoders/exr
 */

import {
    REC709_TO_ACEScg, ACES_AP0_TO_ACEScg, ACES_AP1_TO_ACEScg,
    BT2020_TO_ACEScg, deriveMatrixFromChromaticities,
} from '../../color/spaces.js';

let exrsApi = null;
let loadPromise = null;

/**
 * Lazy-load exrs via the loader shim (public/vendor/exrs/loader.js).
 * Vite forbids `import()` from /public into src/ ESM modules; the
 * loader injects a `<script type="module">` that does the static
 * import natively, then exposes the API on `window.__BlrrdExrs`.
 *
 * The loader also calls exrs's `init()` (one-shot wasm-bindgen
 * runtime setup), so by the time `blrrd:exrs-ready` fires the API is
 * synchronously usable.
 */
function loadExrs() {
    if (exrsApi) return Promise.resolve(exrsApi);
    if (loadPromise) return loadPromise;
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return Promise.reject(new Error('EXR decoder requires a browser environment'));
    }
    loadPromise = new Promise((resolve, reject) => {
        /* Fast path: a previous loadExrs() (or a parallel invocation
         * elsewhere on the page) may have already populated
         * window.__BlrrdExrs. Re-injecting the loader script in that
         * case wouldn't re-fire the ready event because ES modules are
         * cached by URL — the second <script type="module"> with the
         * same src is a no-op for side effects. */
        if (window.__BlrrdExrs && typeof window.__BlrrdExrs.decodeRgbaExr === 'function') {
            exrsApi = window.__BlrrdExrs;
            resolve(exrsApi);
            return;
        }
        /* Stale-failure fast path (Codex r2 STOP): if the loader has
         * already run and rejected (init() threw — wasm fetch fail,
         * CSP violation, corrupt artifact), re-injecting the script
         * cannot recover (module URL cached). Surface the original
         * error fail-fast instead of waiting for an event that will
         * never fire. */
        if (window.__BlrrdExrsError) {
            reject(window.__BlrrdExrsError);
            return;
        }
        const onReady = () => {
            window.removeEventListener('blrrd:exrs-ready', onReady);
            window.removeEventListener('blrrd:exrs-failed', onFail);
            const api = window.__BlrrdExrs;
            if (!api || typeof api.decodeRgbaExr !== 'function') {
                reject(new Error('exrs loader fired ready but API missing on window'));
                return;
            }
            exrsApi = api;
            resolve(api);
        };
        const onFail = () => {
            window.removeEventListener('blrrd:exrs-ready', onReady);
            window.removeEventListener('blrrd:exrs-failed', onFail);
            const err = window.__BlrrdExrsError;
            reject(err || new Error('exrs loader reported failure'));
        };
        window.addEventListener('blrrd:exrs-ready', onReady, { once: true });
        window.addEventListener('blrrd:exrs-failed', onFail, { once: true });
        const script = document.createElement('script');
        script.type = 'module';
        script.src = '/vendor/exrs/loader.js';
        script.onerror = () => {
            window.removeEventListener('blrrd:exrs-ready', onReady);
            window.removeEventListener('blrrd:exrs-failed', onFail);
            reject(new Error('exrs loader script failed to fetch (network or CSP)'));
        };
        document.head.appendChild(script);
    }).catch((err) => {
        loadPromise = null;
        throw err;
    });
    return loadPromise;
}

/* ── Header parsing ───────────────────────────────────────────────── */

/**
 * Magic bytes that mark the start of an OpenEXR file. Same constant the
 * format dispatcher uses — duplicated here so the parser is self-
 * contained (it returns null for non-EXR input rather than throwing).
 */
const EXR_MAGIC = [0x76, 0x2F, 0x31, 0x01];

/**
 * Maximum bytes scanned when looking for the chromaticities attribute.
 * EXR headers are typically a few hundred bytes; capping at 32 KiB
 * comfortably covers multi-layer images while keeping the worst case
 * bounded if the file is corrupt or unexpectedly large.
 */
const MAX_HEADER_SCAN = 32 * 1024;

/**
 * Read a null-terminated ASCII string from `buf` starting at `offset`.
 * Returns an object `{ value, next }` where `next` is the position just
 * after the terminating null byte. Returns null if no terminator is
 * found before `limit` (corrupt or truncated header).
 *
 * @param {Uint8Array} buf
 * @param {number} offset
 * @param {number} limit
 * @returns {{value:string,next:number}|null}
 */
function readNullString(buf, offset, limit) {
    const end = Math.min(buf.length, limit);
    for (let i = offset; i < end; i++) {
        if (buf[i] === 0) {
            let s = '';
            for (let j = offset; j < i; j++) s += String.fromCharCode(buf[j]);
            return { value: s, next: i + 1 };
        }
    }
    return null;
}

/**
 * Parse the `chromaticities` attribute out of an EXR file's header.
 *
 * Returns null when the attribute is absent — per OpenEXR spec, callers
 * should default to linear-Rec709 in that case (matches `decodeExr`'s
 * own behaviour).
 *
 * Returns null on any parse failure (bad magic, truncated header,
 * bogus attribute size). The caller's contract is that we never throw —
 * a missing or unparseable chromaticities attribute is a soft fallback,
 * not a decode-failure condition (the pixels still survive a
 * linear-Rec709 ingest, just possibly with the wrong gamut tag).
 *
 * Attribute byte layout (little-endian on disk):
 *   name (null-terminated ASCII)   "chromaticities\0"
 *   type (null-terminated ASCII)   "chromaticities\0"
 *   size (int32 LE)                 32
 *   value (8 × float32 LE)          redX redY greenX greenY blueX blueY whiteX whiteY
 *
 * @param {Uint8Array} buf
 * @returns {{redX:number,redY:number,greenX:number,greenY:number,
 *            blueX:number,blueY:number,whiteX:number,whiteY:number}|null}
 */
export function parseChromaticities(buf) {
    if (!buf || buf.length < 8) return null;
    /* Magic check — bail fast on non-EXR data so the parser is safe to
     * invoke speculatively. */
    for (let i = 0; i < 4; i++) if (buf[i] !== EXR_MAGIC[i]) return null;

    /* Attributes start right after the 4-byte magic + 4-byte version
     * field. The attribute table is a list of (name, type, size, value)
     * tuples terminated by a single 0x00 byte (empty-name marker). */
    let p = 8;
    const limit = Math.min(buf.length, MAX_HEADER_SCAN);
    /* Use a DataView once for the size + f32 reads; offset into it as
     * we scan, but bound every read against `limit` to stay defensive. */
    const view = new DataView(buf.buffer, buf.byteOffset, buf.length);

    while (p < limit) {
        if (buf[p] === 0) return null;       /* end-of-header — no chromaticities */

        const nameTok = readNullString(buf, p, limit);
        if (!nameTok) return null;
        p = nameTok.next;

        const typeTok = readNullString(buf, p, limit);
        if (!typeTok) return null;
        p = typeTok.next;

        if (p + 4 > limit) return null;
        const size = view.getInt32(p, /* littleEndian */ true);
        p += 4;
        if (size < 0 || size > limit - p) return null;

        if (nameTok.value === 'chromaticities' &&
            typeTok.value === 'chromaticities' &&
            size === 32) {
            /* Read 8 little-endian f32s. The struct order is fixed by
             * the OpenEXR spec. */
            return {
                redX:   view.getFloat32(p,      true),
                redY:   view.getFloat32(p +  4, true),
                greenX: view.getFloat32(p +  8, true),
                greenY: view.getFloat32(p + 12, true),
                blueX:  view.getFloat32(p + 16, true),
                blueY:  view.getFloat32(p + 20, true),
                whiteX: view.getFloat32(p + 24, true),
                whiteY: view.getFloat32(p + 28, true),
            };
        }
        p += size;
    }
    return null;
}

/* ── Detection ────────────────────────────────────────────────────── */

/* Reference primary sets — all tolerances are checked against these.
 * Numbers are CIE-xy coordinates from the same authoritative sources the
 * matrix derivation in src/color/spaces.js uses (BT.709 / SMPTE ST 2065-1
 * / Academy S-2014-004 / BT.2020). Repeating them here keeps the
 * detector self-contained — the matrix loader checks values, the
 * detector checks names. */
const REF_REC709 = {
    redX: 0.640,    redY: 0.330,
    greenX: 0.300,  greenY: 0.600,
    blueX: 0.150,   blueY: 0.060,
    whiteX: 0.3127, whiteY: 0.3290,
};
const REF_AP0 = {
    redX: 0.7347,   redY: 0.2653,
    greenX: 0.0,    greenY: 1.0,
    blueX: 0.0001,  blueY: -0.077,
    whiteX: 0.32168, whiteY: 0.33767,
};
const REF_AP1 = {
    redX: 0.713,    redY: 0.293,
    greenX: 0.165,  greenY: 0.830,
    blueX: 0.128,   blueY: 0.044,
    whiteX: 0.32168, whiteY: 0.33767,
};
const REF_BT2020 = {
    redX: 0.708,    redY: 0.292,
    greenX: 0.170,  greenY: 0.797,
    blueX: 0.131,   blueY: 0.046,
    whiteX: 0.3127, whiteY: 0.3290,
};

/**
 * Tolerance for chromaticity matching. A spec value of 0.640 vs a stored
 * 0.6400001 should still match, and small encoder rounding (e.g. files
 * that store 0.06 as 0.0600001) is also fine. 1e-3 is wide enough for
 * float-rounding wobble but narrow enough to never collide between two
 * spec sets (the gap between adjacent primaries is always > 1e-2).
 */
const CHROMATICITY_TOLERANCE = 1e-3;

function chromMatches(a, b, tol = CHROMATICITY_TOLERANCE) {
    return Math.abs(a.redX   - b.redX)   <= tol
        && Math.abs(a.redY   - b.redY)   <= tol
        && Math.abs(a.greenX - b.greenX) <= tol
        && Math.abs(a.greenY - b.greenY) <= tol
        && Math.abs(a.blueX  - b.blueX)  <= tol
        && Math.abs(a.blueY  - b.blueY)  <= tol
        && Math.abs(a.whiteX - b.whiteX) <= tol
        && Math.abs(a.whiteY - b.whiteY) <= tol;
}

/**
 * Map an EXR chromaticities struct (or null for "absent") to a
 * canonical colour-space tag. Returns 'linear-rec709' for absent /
 * Rec.709-matching primaries, otherwise the matching standard. Falls
 * back to 'unknown' for non-standard primary sets (rare — a custom
 * camera log space, an encoder bug, or someone's experimental gamut).
 *
 * @param {ReturnType<typeof parseChromaticities>|null} chrom
 * @returns {'linear-rec709'|'aces-ap0'|'aces-ap1'|'bt2020'|'unknown'}
 */
export function detectColorSpace(chrom) {
    if (!chrom) return 'linear-rec709';
    if (chromMatches(chrom, REF_REC709)) return 'linear-rec709';
    if (chromMatches(chrom, REF_AP0))    return 'aces-ap0';
    if (chromMatches(chrom, REF_AP1))    return 'aces-ap1';
    if (chromMatches(chrom, REF_BT2020)) return 'bt2020';
    return 'unknown';
}

/* ── Matrix application ───────────────────────────────────────────── */

/**
 * Apply a 3×3 source-RGB → ACEScg matrix to an interleaved RGBA
 * Float32 buffer in-place. Alpha is left untouched. Used after un-
 * premultiply so the kernel sees ACEScg straight alpha.
 *
 * @param {Float32Array} rgba
 * @param {ArrayLike<number>} m   row-major 3×3
 */
function applyMatrixInPlace(rgba, m) {
    const m0 = m[0], m1 = m[1], m2 = m[2];
    const m3 = m[3], m4 = m[4], m5 = m[5];
    const m6 = m[6], m7 = m[7], m8 = m[8];
    const N = rgba.length;
    for (let i = 0; i < N; i += 4) {
        const r = rgba[i];
        const g = rgba[i + 1];
        const b = rgba[i + 2];
        rgba[i]     = m0 * r + m1 * g + m2 * b;
        rgba[i + 1] = m3 * r + m4 * g + m5 * b;
        rgba[i + 2] = m6 * r + m7 * g + m8 * b;
        /* alpha (i+3) untouched */
    }
}

/**
 * Pick the source-→ACEScg matrix for a known colour-space tag. Returns
 * null when no transform is required (linear-Rec709 stays as-is so the
 * downstream legacy ingest path can run M_sRGB_to_ACEScg there).
 *
 * For 'unknown' we derive the matrix from the supplied chromaticities
 * struct — the only path that uses arbitrary primaries.
 *
 * @param {string} tag
 * @param {ReturnType<typeof parseChromaticities>|null} chrom
 * @returns {Float32Array|null}
 */
function matrixForTag(tag, chrom) {
    if (tag === 'aces-ap0') return ACES_AP0_TO_ACEScg;
    if (tag === 'aces-ap1') return ACES_AP1_TO_ACEScg;   /* identity */
    if (tag === 'bt2020')   return BT2020_TO_ACEScg;
    if (tag === 'unknown' && chrom) {
        try { return deriveMatrixFromChromaticities(chrom); }
        catch { return null; }    /* singular matrix → fall back to no-op */
    }
    return null;       /* 'linear-rec709' / 'srgb' — handled downstream */
}

/* ── Un-premultiply ───────────────────────────────────────────────── */

/**
 * Un-premultiply RGB by alpha in-place (Codex 3.C-design). OpenEXR
 * convention: color channels are premultiplied with alpha. BLRRD's
 * downstream kernel + view-transform expect straight alpha, so we
 * divide out alpha for partially-transparent pixels. Pixels with
 * alpha=0 stay zero (best-effort — emissive ALPHA=0 pixels are an
 * edge case the EXR spec acknowledges; we preserve their RGB
 * intentionally undefined, matching the spec).
 *
 * @param {Float32Array} rgba   interleaved RGBA, length = w*h*4
 */
function unpremultiplyRGBA(rgba) {
    for (let i = 0; i < rgba.length; i += 4) {
        const a = rgba[i + 3];
        if (a > 0 && a < 1) {
            const inv = 1 / a;
            rgba[i]     *= inv;
            rgba[i + 1] *= inv;
            rgba[i + 2] *= inv;
        }
        /* a === 0: keep RGB as-is (see header note).
         * a >= 1: nothing to do (premultiply is a no-op at full alpha). */
    }
}

/**
 * Friendly error tag. Mirrors the libraw decoder's wrapping pattern
 * so the legacy editor's UnsupportedFormatError handler shows a
 * useful toast.
 */
function wrapExrError(stage, err) {
    const msg = (err && err.message) || String(err);
    const e = new Error(`EXR decode failed (${stage}): ${msg}. ` +
        `If this EXR uses an exotic compression (DWAA / DWAB), please ` +
        `re-export as Zip / PIZ / RLE / PXR24 / B44.`);
    e.name = 'UnsupportedFormatError';
    e.format = 'exr';
    return e;
}

/* ── Manual override coercion ─────────────────────────────────────── */

/**
 * Normalise a manual-override colour-space hint to the canonical tag
 * set the dispatcher uses. Forgiving on case + common aliases (e.g.
 * 'rec709', 'linear-rec.709', 'ACEScg', 'aces2065-1') so the override
 * is friendly to both UI strings and exr2aces-style abbreviations.
 * Returns null if the hint is unrecognised — caller falls back to
 * automatic detection.
 *
 * @param {string} hint
 * @returns {'linear-rec709'|'aces-ap0'|'aces-ap1'|'bt2020'|null}
 */
function coerceColorSpaceHint(hint) {
    if (!hint || typeof hint !== 'string') return null;
    const k = hint.toLowerCase().replace(/[._\s]/g, '-');
    if (k === 'linear-rec709' || k === 'rec709' || k === 'linear-rec-709' ||
        k === 'srgb' || k === 'linear-srgb') return 'linear-rec709';
    if (k === 'aces-ap0' || k === 'ap0' || k === 'aces2065-1' ||
        k === 'aces20651' || k === 'aces-2065-1') return 'aces-ap0';
    if (k === 'aces-ap1' || k === 'ap1' || k === 'acescg') return 'aces-ap1';
    if (k === 'bt2020' || k === 'bt-2020' || k === 'rec2020' ||
        k === 'rec-2020' || k === 'rec-bt2020') return 'bt2020';
    return null;
}

/**
 * Decode an OpenEXR blob to ACEScg-ingestable Float32 RGBA.
 *
 * @param {Blob} blob
 * @param {{colorSpace?:string}} [opts]
 *   `opts.colorSpace`: manual override that bypasses chromaticities
 *   detection. Useful when the EXR has no `chromaticities` attribute
 *   but the user knows the source space (e.g. an unmarked render from
 *   an ACES pipeline). Accepted aliases are forgiving — see
 *   `coerceColorSpaceHint`.
 * @returns {Promise<{kind:'rgbaFloat',rgba:Float32Array,w:number,h:number,
 *                   colorSpace:'linear-rec709'|'aces-ap1',bitDepth:32,
 *                   exr?:{detectedColorSpace:string,
 *                         chromaticitiesPresent:boolean,
 *                         override?:string,
 *                         matrixDerivationFailed?:boolean}}>}
 *
 * Diagnostic flags on the `exr` field:
 *   - `detectedColorSpace`     — one of 'linear-rec709' | 'aces-ap0' |
 *     'aces-ap1' | 'bt2020' | 'unknown'.
 *   - `chromaticitiesPresent`  — whether the file declared a primaries set.
 *   - `override`               — set when `opts.colorSpace` short-circuited
 *     auto-detection.
 *   - `matrixDerivationFailed` — set when a non-spec primary set could not
 *     be turned into a finite source-→ACEScg matrix; the decoder then
 *     falls back to the legacy linear-Rec709 ingest path so the file is
 *     still labelled truthfully (Codex 3.C.1 r1 hardening).
 */
export async function decode(blob, opts = {}) {
    if (!blob || typeof blob.arrayBuffer !== 'function') {
        throw new Error('EXR decoder expected a Blob input');
    }

    /* Wrap loader rejection in wrapExrError so the legacy editor's
     * UnsupportedFormatError-only toast handler fires (Codex r3 STOP:
     * raw loader errors fall through to the native <img> path
     * otherwise). Covers both first-attempt failures and the
     * stale-error fast-path on subsequent decode attempts. */
    let api;
    try { api = await loadExrs(); }
    catch (err) { throw wrapExrError('load', err); }

    /* Read buffer BEFORE any wasm-side state allocation — same
     * pattern as raw.js: a blob.arrayBuffer() rejection should not
     * leak any wasm memory. */
    const buf = new Uint8Array(await blob.arrayBuffer());

    /* Parse chromaticities BEFORE the WASM decode call so we never
     * lose access to the input bytes (the WASM call doesn't mutate
     * the JS-side buffer, but reading once up-front keeps the control
     * flow linear and the parse pure). */
    const chrom = parseChromaticities(buf);
    const overrideHint = coerceColorSpaceHint(opts && opts.colorSpace);
    const detectedTag = overrideHint || detectColorSpace(chrom);

    let img;
    let rgba;
    let w, h;
    try {
        img = api.decodeRgbaExr(buf);
    } catch (err) {
        /* RGBA fast-path failed — try the RGB fast-path before giving
         * up. Many beauty passes are 3-channel. */
        try {
            const rgbImg = api.decodeRgbExr(buf);
            const rgb = rgbImg.interleavedRgbPixels;
            w = rgbImg.width; h = rgbImg.height;
            rgba = new Float32Array(w * h * 4);
            for (let i = 0, j = 0; i < rgb.length; i += 3, j += 4) {
                rgba[j]     = rgb[i];
                rgba[j + 1] = rgb[i + 1];
                rgba[j + 2] = rgb[i + 2];
                rgba[j + 3] = 1;
            }
        } catch (err2) {
            throw wrapExrError('decode', err2);
        }
    }

    if (!rgba) {
        if (!img || typeof img.width !== 'number' || typeof img.height !== 'number') {
            throw wrapExrError('decode',
                new Error('decodeRgbaExr returned no width/height'));
        }
        rgba = img.interleavedRgbaPixels;
        w = img.width; h = img.height;
        if (!rgba || rgba.length !== w * h * 4) {
            throw wrapExrError('decode',
                new Error(`pixel buffer length=${rgba && rgba.length} mismatches dims ${w}×${h}`));
        }
    }

    /* Un-premultiply alpha so downstream kernel sees straight alpha
     * (Codex 3.C-design Q6). Run BEFORE the chromaticities matrix —
     * the matrix is a linear operator on RGB, so applying it to the
     * straight-alpha values matches what an OCIO IDT would do. */
    unpremultiplyRGBA(rgba);

    /* Best-effort cleanup of any wasm-side handle exrs may attach
     * (the package readme says .free() is exposed but optional —
     * GC otherwise). Optional-chaining keeps it safe across
     * versions. */
    try { img && img.free?.(); } catch { /* ignore */ }

    /* Dispatch on the detected colour space. Three landing zones:
     *
     *   1. linear-Rec709 (or absent chromaticities) → return as
     *      'linear-rec709'. The downstream ingest path will run
     *      ingestLinearRec709 (= M_sRGB_to_ACEScg) once into ACEScg.
     *      This preserves the byte-identical Phase 3.C v1 contract
     *      for the dominant case.
     *
     *   2. AP1 (= ACEScg) → identity matrix. We tag 'aces-ap1' so the
     *      legacy ingest path runs `ingestLinearACEScg` (a copy
     *      passthrough), which is correct and zero-cost matrix-wise.
     *
     *   3. AP0 / BT.2020 / unknown → apply the source-→ACEScg matrix
     *      in-place here, then tag 'aces-ap1' for downstream identity
     *      ingest. Doing the matrix in the decoder (rather than
     *      teaching the dispatcher about every primary set) keeps the
     *      ingest contract narrow.
     */
    let outColorSpace = 'linear-rec709';
    let unknownFallback = false;
    if (detectedTag !== 'linear-rec709') {
        const mat = matrixForTag(detectedTag, chrom);
        if (detectedTag === 'aces-ap1') {
            /* Identity — no matrix mul, but tag as ACEScg so the
             * downstream ingest runs the copy-passthrough. */
            outColorSpace = 'aces-ap1';
        } else if (mat && isFiniteMatrix(mat)) {
            applyMatrixInPlace(rgba, mat);
            outColorSpace = 'aces-ap1';
        } else {
            /* 'unknown' primaries with a degenerate / non-finite
             * derivation result (Codex 3.C.1 r1 hardening): we cannot
             * safely transform, so don't lie about the colour space.
             * Fall through to the legacy `linear-rec709` ingest path —
             * the result is "wrong" in colour but at least labelled
             * truthfully, and the downstream ingest still produces a
             * usable image. The diagnostic field surfaces the failure
             * for UI / log inspection. */
            outColorSpace = 'linear-rec709';
            unknownFallback = true;
        }
    }

    return {
        kind:       'rgbaFloat',
        rgba,
        w,
        h,
        colorSpace: outColorSpace,
        bitDepth:   32,
        /* Diagnostics — the decoder reports what it actually saw. UI
         * can surface this in a "source space" badge, tests assert on
         * it, debugging is easier when the file's tag is visible. */
        exr: {
            detectedColorSpace:     detectedTag,
            chromaticitiesPresent:  !!chrom,
            ...(overrideHint    ? { override: overrideHint }     : {}),
            ...(unknownFallback ? { matrixDerivationFailed: true } : {}),
        },
    };
}

/**
 * Cheap finiteness check for a 9-element matrix. Guards against the
 * `deriveMatrixFromChromaticities` path producing NaN / ±Infinity for
 * a malformed `chromaticities` attribute (e.g. all-zero blue primary
 * → singular RGB-to-XYZ → division spike). A single non-finite entry
 * would propagate through the inner-loop multiplies and corrupt every
 * pixel, so we'd rather skip the dispatch in that case.
 *
 * @param {ArrayLike<number>} m
 * @returns {boolean}
 */
function isFiniteMatrix(m) {
    for (let i = 0; i < 9; i++) {
        if (!Number.isFinite(m[i])) return false;
    }
    return true;
}
